import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { db } from "@/api/db";
import { GraduationCap, Clock, DollarSign, Target, Zap, ChevronRight, ArrowLeft, CheckCircle } from "lucide-react";

const STEPS = [
  { id: "welcome", label: "Boas-vindas" },
  { id: "time", label: "Tempo" },
  { id: "money", label: "Situação financeira" },
  { id: "deadline", label: "Prazo" },
  { id: "urgency", label: "Urgência" },
  { id: "result", label: "Seu cenário" },
];

const MODES = {
  Acelerado: {
    color: "#E5FF52",
    bg: "#65541B",
    desc: "Você tem urgência e energia. Vamos montar um plano intenso, focado em resultado rápido.",
    icon: "⚡",
  },
  Seguro: {
    color: "#00DCA4",
    bg: "#00664C",
    desc: "Você pode transicionar com calma, sem abrir mão da segurança. Ritmo consistente e sustentável.",
    icon: "🛡️",
  },
  Exploração: {
    color: "#B0FCEA",
    bg: "#003326",
    desc: "Você ainda está descobrindo. Vamos explorar opções antes de decidir o caminho.",
    icon: "🧭",
  },
};

function calcMode(data) {
  const { urgency_level, transition_deadline, has_reserve, hours_per_week } = data;
  if (
    urgency_level === "alto" ||
    transition_deadline === "Quero mudar já (0–3 meses)" ||
    (transition_deadline === "Em 3 a 6 meses" && urgency_level === "médio")
  ) return "Acelerado";
  if (transition_deadline === "Sem prazo definido" || urgency_level === "baixo") return "Exploração";
  return "Seguro";
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    hours_per_week: null,
    current_income: null,
    has_reserve: null,
    transition_deadline: null,
    urgency_level: null,
  });
  const [mode, setMode] = useState(null);

  useEffect(() => {
    db.auth.me().then(u => {
      setUser(u);
      // Check if already completed
      db.entities.OnboardingProfile.filter({ user_email: u.email }, "-created_date", 1)
        .then(profiles => {
          if (profiles[0]?.completed) navigate(createPageUrl("Home"));
        });
    });
  }, []);

  const set = (key, value) => setData(d => ({ ...d, [key]: value }));

  const handleNext = async () => {
    if (step === 4) {
      // calculate mode & save
      const calculatedMode = calcMode(data);
      setMode(calculatedMode);
      setSaving(true);
      const existing = await db.entities.OnboardingProfile.filter({ user_email: user.email }, "-created_date", 1);
      const payload = { ...data, user_email: user.email, mode: calculatedMode, completed: true };
      if (existing[0]) {
        await db.entities.OnboardingProfile.update(existing[0].id, payload);
      } else {
        await db.entities.OnboardingProfile.create(payload);
      }
      setSaving(false);
      setStep(5);
      return;
    }
    setStep(s => s + 1);
  };

  const canNext = () => {
    if (step === 0) return true;
    if (step === 1) return data.hours_per_week !== null;
    if (step === 2) return data.current_income && data.has_reserve;
    if (step === 3) return data.transition_deadline !== null;
    if (step === 4) return data.urgency_level !== null;
    return true;
  };

  const progress = (step / (STEPS.length - 1)) * 100;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: "#F2F4F4" }}>
      {/* Card */}
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg overflow-hidden">

        {/* Top bar */}
        {step > 0 && step < 5 && (
          <div className="px-6 pt-5 pb-0">
            <div className="flex items-center justify-between mb-3">
              <button onClick={() => setStep(s => s - 1)} className="flex items-center gap-1 text-sm" style={{ color: "#99A6AB" }}>
                <ArrowLeft className="w-4 h-4" /> Voltar
              </button>
              <span className="text-xs font-medium" style={{ color: "#99A6AB" }}>
                {step} de {STEPS.length - 2}
              </span>
            </div>
            <div className="w-full rounded-full h-1.5" style={{ background: "#E5E9EA" }}>
              <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: "#00DCA4" }} />
            </div>
          </div>
        )}

        <div className="p-6 lg:p-8">

          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className="text-center space-y-5">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto" style={{ background: "#00DCA4" }}>
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
                  Bem-vindo à Portüs 👋
                </h1>
                <p className="text-sm leading-relaxed" style={{ color: "#646C6F" }}>
                  Antes de começar, queremos entender o seu momento real — tempo disponível, situação financeira e o quanto você quer mudar.
                </p>
                <p className="text-sm mt-3 font-medium" style={{ color: "#00664C" }}>
                  Com isso, montamos um plano feito pra você.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                {[["🎯", "Clareza"], ["🗺️", "Validação"], ["💰", "Renda"]].map(([icon, label]) => (
                  <div key={label} className="rounded-xl p-3" style={{ background: "#EAFAF6" }}>
                    <div className="text-xl mb-1">{icon}</div>
                    <p className="text-xs font-semibold" style={{ color: "#00664C" }}>{label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Time */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#EAFAF6" }}>
                  <Clock className="w-5 h-5" style={{ color: "#00664C" }} />
                </div>
                <div>
                  <h2 className="font-bold text-lg" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>Quanto tempo você tem?</h2>
                  <p className="text-xs" style={{ color: "#99A6AB" }}>Por semana, fora do trabalho atual</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["1–3 horas", 2],
                  ["4–7 horas", 5],
                  ["8–14 horas", 10],
                  ["15h ou mais", 15],
                ].map(([label, val]) => (
                  <button
                    key={val}
                    onClick={() => set("hours_per_week", val)}
                    className="p-4 rounded-xl border-2 text-sm font-semibold transition-all text-left"
                    style={{
                      borderColor: data.hours_per_week === val ? "#00DCA4" : "#E5E9EA",
                      background: data.hours_per_week === val ? "#EAFAF6" : "#fff",
                      color: data.hours_per_week === val ? "#00664C" : "#2E3233",
                    }}
                  >
                    {data.hours_per_week === val && <CheckCircle className="w-4 h-4 mb-1" style={{ color: "#00DCA4" }} />}
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Money */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#EAFAF6" }}>
                  <DollarSign className="w-5 h-5" style={{ color: "#00664C" }} />
                </div>
                <div>
                  <h2 className="font-bold text-lg" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>Situação financeira</h2>
                  <p className="text-xs" style={{ color: "#99A6AB" }}>Isso nos ajuda a calibrar o ritmo</p>
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2" style={{ color: "#2E3233" }}>Renda mensal atual</p>
                <div className="grid grid-cols-1 gap-2">
                  {["Sem renda", "Até R$2k", "R$2k–5k", "R$5k–10k", "Acima de R$10k"].map(opt => (
                    <button
                      key={opt}
                      onClick={() => set("current_income", opt)}
                      className="p-3 rounded-xl border-2 text-sm font-medium text-left transition-all"
                      style={{
                        borderColor: data.current_income === opt ? "#00DCA4" : "#E5E9EA",
                        background: data.current_income === opt ? "#EAFAF6" : "#fff",
                        color: data.current_income === opt ? "#00664C" : "#2E3233",
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2" style={{ color: "#2E3233" }}>Reserva financeira</p>
                <div className="grid grid-cols-1 gap-2">
                  {["Não tenho reserva", "Menos de 3 meses", "3 a 6 meses", "Mais de 6 meses"].map(opt => (
                    <button
                      key={opt}
                      onClick={() => set("has_reserve", opt)}
                      className="p-3 rounded-xl border-2 text-sm font-medium text-left transition-all"
                      style={{
                        borderColor: data.has_reserve === opt ? "#00DCA4" : "#E5E9EA",
                        background: data.has_reserve === opt ? "#EAFAF6" : "#fff",
                        color: data.has_reserve === opt ? "#00664C" : "#2E3233",
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Deadline */}
          {step === 3 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#EAFAF6" }}>
                  <Target className="w-5 h-5" style={{ color: "#00664C" }} />
                </div>
                <div>
                  <h2 className="font-bold text-lg" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>Quando você quer mudar?</h2>
                  <p className="text-xs" style={{ color: "#99A6AB" }}>Prazo desejado para a transição</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {[
                  "Quero mudar já (0–3 meses)",
                  "Em 3 a 6 meses",
                  "Em 6 a 12 meses",
                  "Sem prazo definido",
                ].map(opt => (
                  <button
                    key={opt}
                    onClick={() => set("transition_deadline", opt)}
                    className="p-4 rounded-xl border-2 text-sm font-semibold text-left transition-all"
                    style={{
                      borderColor: data.transition_deadline === opt ? "#00DCA4" : "#E5E9EA",
                      background: data.transition_deadline === opt ? "#EAFAF6" : "#fff",
                      color: data.transition_deadline === opt ? "#00664C" : "#2E3233",
                    }}
                  >
                    {data.transition_deadline === opt && <CheckCircle className="w-4 h-4 mb-1" style={{ color: "#00DCA4" }} />}
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Urgency */}
          {step === 4 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#EAFAF6" }}>
                  <Zap className="w-5 h-5" style={{ color: "#00664C" }} />
                </div>
                <div>
                  <h2 className="font-bold text-lg" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>Qual o seu nível de urgência?</h2>
                  <p className="text-xs" style={{ color: "#99A6AB" }}>O quanto essa mudança está pressionando você agora</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {[
                  { val: "baixo", label: "Baixo", desc: "Estou explorando, sem pressa", emoji: "😌" },
                  { val: "médio", label: "Médio", desc: "Quero mudar, mas tenho tempo", emoji: "🎯" },
                  { val: "alto", label: "Alto", desc: "Preciso mudar logo, sinto urgência real", emoji: "🔥" },
                ].map(opt => (
                  <button
                    key={opt.val}
                    onClick={() => set("urgency_level", opt.val)}
                    className="p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4"
                    style={{
                      borderColor: data.urgency_level === opt.val ? "#00DCA4" : "#E5E9EA",
                      background: data.urgency_level === opt.val ? "#EAFAF6" : "#fff",
                    }}
                  >
                    <span className="text-2xl">{opt.emoji}</span>
                    <div>
                      <p className="text-sm font-bold" style={{ color: data.urgency_level === opt.val ? "#00664C" : "#2E3233" }}>{opt.label}</p>
                      <p className="text-xs" style={{ color: "#99A6AB" }}>{opt.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Result */}
          {step === 5 && mode && (
            <div className="text-center space-y-6">
              <div
                className="rounded-2xl p-6 space-y-3"
                style={{ background: MODES[mode].bg }}
              >
                <div className="text-4xl">{MODES[mode].icon}</div>
                <div>
                  <p className="text-sm font-medium mb-1" style={{ color: MODES[mode].color, opacity: 0.8 }}>Seu modo de trilha</p>
                  <h2 className="text-3xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: MODES[mode].color }}>
                    Modo {mode}
                  </h2>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: MODES[mode].color, opacity: 0.9 }}>{MODES[mode].desc}</p>
              </div>

              {/* Summary */}
              <div className="portus-card text-left space-y-3">
                <p className="text-sm font-bold" style={{ color: "#003326" }}>Seu cenário atual</p>
                {[
                  ["⏱️ Tempo disponível", `${data.hours_per_week}h/semana`],
                  ["💰 Renda atual", data.current_income],
                  ["🏦 Reserva", data.has_reserve],
                  ["📅 Prazo desejado", data.transition_deadline],
                  ["⚡ Urgência", data.urgency_level],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: "#646C6F" }}>{label}</span>
                    <span className="text-xs font-semibold" style={{ color: "#003326" }}>{value}</span>
                  </div>
                ))}
              </div>

              <p className="text-xs" style={{ color: "#99A6AB" }}>
                Próximo passo: diagnóstico de carreira para definir seu Top 3 caminhos.
              </p>
            </div>
          )}

          {/* CTA Button */}
          <div className="mt-8">
            {step < 5 ? (
              <button
                onClick={handleNext}
                disabled={!canNext() || saving}
                className="w-full py-3.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-opacity disabled:opacity-40"
                style={{ background: "#00DCA4", color: "#003326" }}
              >
                {saving ? "Salvando..." : step === 4 ? "Ver meu cenário →" : step === 0 ? "Começar" : "Continuar"}
                {!saving && step < 4 && <ChevronRight className="w-4 h-4" />}
              </button>
            ) : (
              <button
                onClick={() => navigate(createPageUrl("Home"))}
                className="w-full py-3.5 rounded-full font-bold text-sm flex items-center justify-center gap-2"
                style={{ background: "#00DCA4", color: "#003326" }}
              >
                Ir para a plataforma <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}